/**
 * Module 4: Le Toit de la Tonnelle - Types TypeScript
 * 
 * Définitions des types selon le cahier des charges
 * Architecture modulaire stricte - Isolation totale
 * 
 * Tâches couvertes:
 * - Tâche 4.1: Génération des Nervures du Toit ("Baleines")
 * - Tâche 4.2: Mécanisme de Génération du Toit
 * - Tâche 4.3: Bibliothèque de Styles Prédéfinis
 * - Tâche 4.4: Outil de Dessin Personnalisé (Canvas 2D)
 * - Tâche 4.5: Adaptation Dynamique Cruciale de la Longueur des Baleines
 */

import * as THREE from 'three';

/**
 * Types de styles prédéfinis pour les baleines du toit
 * Tâche 4.3: Bibliothèque de Styles Prédéfinis
 */
export enum TypeStyleBaleine {
  CLASSIQUE = 'classique',
  COURBE_DOUCE = 'courbe_douce',
  GOTHIQUE = 'gothique',
  MODERNE = 'moderne',
  ASIATIQUE = 'asiatique',
  PERSONNALISE = 'personnalise'
}

/**
 * Types de matériaux pour le toit
 */
export enum TypeMateriauToit {
  FER_A_BETON = 'fer_a_beton',    // Section ronde
  TUBE_CARRE = 'tube_carre'       // Section carrée
}

/**
 * Informations détaillées sur un matériau de toit
 */
export interface InfosMateriauToit {
  nom: string;
  densite: number;
  resistanceTraction: number;
  moduleElasticite: number;
  couleur: string;
}

/**
 * Point 2D pour le canvas de dessin personnalisé des baleines
 * Tâche 4.4: Outil de Dessin Personnalisé
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Point 3D pour géométrie
 */
export interface Point3D {
  x: number;
  y: number;
  z: number;
}

/**
 * Courbe de Bézier pour les baleines personnalisées
 * Tâche 4.4: Canvas 2D pour dessiner les baleines
 */
export interface CourbeBezier {
  id?: string;
  debut: Point2D;
  fin: Point2D;
  controle1: Point2D;
  controle2: Point2D;
  selected?: boolean;
}

// Alias pour compatibilité
export type CourbeBezierBaleine = CourbeBezier;

/**
 * Ligne droite pour les baleines personnalisées
 */
export interface LigneDroite {
  id?: string;
  debut: Point2D;
  fin: Point2D;
  selected?: boolean;
}

// Alias pour compatibilité
export type LigneDroiteBaleine = LigneDroite;

/**
 * Types d'outils de dessin disponibles pour les baleines
 */
export enum OutilDessin {
  CRAYON = 'crayon',
  LIGNE = 'ligne',
  BEZIER = 'bezier',
  SELECTION = 'selection'
}

// Alias pour compatibilité
export const OutilDessinBaleine = OutilDessin;

/**
 * État de l'outil de sélection pour les baleines
 */
export interface EtatSelection {
  actif: boolean;
  elementSelectionne?: string;
  typeElement?: 'ligne' | 'bezier' | 'crayon';
  poigneeActive?: 'debut' | 'fin' | 'controle1' | 'controle2';
  element?: string;
  pointDebut?: Point2D;
}

// Alias pour compatibilité
export type EtatSelectionBaleine = EtatSelection;

/**
 * Configuration du canvas 2D pour l'outil de dessin
 * Tâche 4.4: Outil de Dessin Personnalisé
 */
export interface ConfigurationCanvas2D {
  largeur: number;
  hauteur: number;
  couleurFond: string;
  couleurGrille: string;
  couleurDessin: string;
  couleurSelection: string;
  epaisseurLigne: number;
  taillePoint: number;
  afficherGrille: boolean;
  magnetisme: boolean;
  toleranceSelection: number;
}

/**
 * Profil personnalisé de baleine dessiné sur le canvas 2D
 * Tâche 4.4: Outil de Dessin Personnalisé
 */
export interface ProfilBaleinePersonnalise {
  id: string;
  nom: string;
  largeur: number;
  hauteur: number;
  courbes: CourbeBezier[];
  lignes: Point2D[][]; // Chemins de crayon
  lignesDroites: LigneDroite[];
  profil: Point2D[]; // Profil normalisé pour la génération 3D
}

/**
 * Événements du canvas 2D
 */
export interface EvenementCanvas {
  type: 'mousedown' | 'mousemove' | 'mouseup' | 'click' | 'dblclick' | 'dessin_commence' | 'dessin_termine' | 'outil_change' | 'efface' | 'annule' | 'refait';
  position?: Point2D;
  bouton?: number;
  touches?: {
    shift: boolean;
    ctrl: boolean;
    alt: boolean;
  };
  outil?: OutilDessin;
  point?: Point2D;
}

/**
 * Paramètres de configuration du toit
 * Tâche 4.1: Génération des Nervures du Toit
 * Note: matériau, épaisseur et couleur sont hérités de la structure de base
 */
export interface ParametresToit {
  actif: boolean; // Activation/désactivation du module toit
  styleBaleine: TypeStyleBaleine;
  profilPersonnalise?: ProfilBaleinePersonnalise;
  hauteurToit: number; // Hauteur générale du toit en mètres
  hauteurConvergence: number; // Hauteur du point de convergence en mètres
  nombreBaleines?: number; // Optionnel, suit automatiquement le nombre de montants
  alignerSurMontants: boolean; // Aligne les baleines exactement sur les montants de la tonnelle
  pointJonction: TypePointJonction;
  rayonPointJonction?: number; // Pour les jonctions circulaires
  aplatirBaleines: boolean; // Contrôle si les baleines s'aplatissent avec la hauteur de convergence
  materiau?: string; // Matériau hérité de la structure de base
  epaisseur?: number; // Épaisseur héritée de la structure de base (en mètres)
  couleur?: string; // Couleur héritée de la structure de base
}

/**
 * Types de points de jonction au sommet
 * Tâche 4.1: Point de Jonction
 */
export enum TypePointJonction {
  POINT_SIMPLE = 'point_simple',
  POUTRE_CIRCULAIRE = 'poutre_circulaire'
}

/**
 * Données de la structure de base nécessaires pour le toit
 * Tâche 4.5: Adaptation Dynamique Cruciale
 */
export interface DonneesStructureBasePourToit {
  forme: string;
  largeur: number;
  longueur?: number;
  hauteur: number;
  nombreMontants: number;
  positionsMontants: Point3D[];
  poutreHaute: {
    points: Point3D[];
    hauteur: number;
  };
  doublePoutreActive?: boolean;
  doublePoutre?: {
    points: Point3D[];
    hauteur: number;
  };
  centre: Point3D;
  materiau: string; // Matériau de la structure de base
  epaisseur?: number; // Épaisseur de la structure de base (optionnelle)
  couleur?: string; // Couleur de la structure de base (optionnelle)
}

/**
 * Géométrie d'une baleine individuelle
 * Tâche 4.2: Mécanisme de Génération du Toit
 */
export interface GeometrieBaleine {
  id: string;
  indexMontant: number;
  pointAttache: Point3D; // Point d'attache sur la poutre haute
  pointConvergence: Point3D; // Point de convergence central
  longueur: number; // Longueur calculée dynamiquement
  angle: number; // Angle par rapport au centre
  profil2D: Point2D[]; // Profil 2D de la baleine
  points3D: Point3D[]; // Points 3D de la géométrie finale
  indices: number[]; // Indices pour les triangles
}

/**
 * Structure complète du toit
 * Tâche 4.1 et 4.2: Structure globale
 */
export interface StructureToit {
  id: string;
  parametres: ParametresToit;
  baleines: GeometrieBaleine[];
  pointConvergence: Point3D;
  pointJonction?: {
    type: TypePointJonction;
    geometrie: Point3D[];
    indices: number[];
  };
}

/**
 * Styles prédéfinis de baleines
 * Tâche 4.3: Bibliothèque de Styles Prédéfinis
 */
export interface StyleBaleinePredefini {
  id: TypeStyleBaleine;
  nom: string;
  description: string;
  profil2D: Point2D[]; // Profil normalisé (0-1)
  apercu: string; // URL ou données de l'aperçu
}

/**
 * Configuration du canvas 2D pour les baleines
 * Tâche 4.4: Canvas 2D
 */
export interface ConfigurationCanvasBaleine {
  largeur: number;
  hauteur: number;
  tailleGrille: number;
  couleurs: {
    grille: string;
    ligne: string;
    bezier: string;
    controle: string;
    fond: string;
  };
}

/**
 * Résultat du calcul de longueur de baleine
 * Tâche 4.5: Adaptation Dynamique Cruciale
 */
export interface CalculLongueurBaleine {
  indexMontant: number;
  pointAttache: Point3D;
  pointConvergence: Point3D;
  longueur: number;
  angle: number;
  distanceAuCentre: number;
}

/**
 * Événements du module toit
 */
export interface EvenementsToit {
  onParametresChange: (parametres: ParametresToit) => void;
  onStructureGenere: (structure: StructureToit) => void;
  onErreur: (erreur: string) => void;
}

/**
 * État du module toit
 */
export interface EtatModuleToit {
  parametres: ParametresToit;
  structure?: StructureToit;
  structureGeneree?: boolean;
  enChargement: boolean;
  erreur?: string;
  erreurs: string[];
  avertissements: string[];
  modeEdition: boolean;
  outilCanvas: OutilDessin;
  profilEnCours?: ProfilBaleinePersonnalise;
  stylesPredefinis: StyleBaleinePredefini[];
  profilsPersonnalises: ProfilBaleinePersonnalise[];
}

/**
 * Configuration par défaut du module
 */
export interface ConfigurationModuleToit {
  nom: string;
  version: string;
  description: string;
  parametresDefaut: ParametresToit;
  canvas: ConfigurationCanvasBaleine;
  stylesPredefinis: StyleBaleinePredefini[];
}